import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from '@tanstack/react-router'
import { AlertTriangle, ArrowLeft, ArrowRight, Check, Send } from 'lucide-react'
import { toast } from 'sonner'
import { SaveIndicator } from '@/components/SaveIndicator'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Field, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import { platformApi } from '../api'
import { AnswersView } from '../components/AnswersView'
import FormRenderer from '../components/FormRenderer'
import { useFormRun } from '../hooks/useFormRun'
import { missingOnPage } from '../lib/form'
import type { FileRecord } from '../types'

/** Wizard for filling in a draft: one step per active page, then review. */
export default function FormRunPage({ submissionId }: { submissionId: string }) {
    const run = useFormRun(submissionId)
    const navigate = useNavigate()
    const [files, setFiles] = useState<FileRecord[]>([])
    const [sentBackReason, setSentBackReason] = useState('')
    const onReviewStep = Boolean(run.definition) && run.step >= run.pages.length
    useEffect(() => {
        if (!onReviewStep) return
        platformApi.listFiles(submissionId).then((r) => setFiles(r.items)).catch(() => undefined)
    }, [onReviewStep, submissionId])

    const returned = run.submission?.status === 'returned'
    useEffect(() => {
        if (!returned) return
        platformApi.getSubmission(submissionId).then((d) => {
            const last = [...d.decisions].reverse().find((x) => x.decision === 'send_back')
            setSentBackReason(last ? `${last.decided_by_name}: “${last.comment}”` : '')
        }).catch(() => undefined)
    }, [returned, submissionId])

    if (run.loading) {
        return <div className="flex justify-center py-24"><Spinner className="size-6" /></div>
    }
    if (run.error || !run.submission || !run.definition) {
        return (
            <div className="mx-auto max-w-xl py-8">
                <Alert variant="destructive"><AlertTriangle /><AlertTitle>Cannot open this form</AlertTitle>
                    <AlertDescription>{run.error ?? 'Not found'}</AlertDescription></Alert>
            </div>
        )
    }
    if (run.submission.status !== 'draft' && run.submission.status !== 'returned') {
        return <Navigate to="/submissions/$id" params={{ id: submissionId }} replace />
    }

    const { pages, step } = run
    const onReview = step >= pages.length
    const page = pages[step]

    const onSubmit = async () => {
        const done = await run.submit()
        if (done) {
            toast.success(`${done.number} ${returned ? 'resubmitted' : 'submitted'}`)
            void navigate({ to: '/submissions/$id', params: { id: submissionId } })
        }
    }

    return (
        <div className="grid gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
            {/* Step list */}
            <aside className="flex flex-col gap-1 lg:sticky lg:top-20 lg:self-start">
                <div className="mb-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{run.submission.form_name}</p>
                    <p className="font-mono text-xs text-muted-foreground">{run.submission.number}</p>
                </div>
                <nav aria-label="Form steps" className="flex flex-row gap-1 overflow-x-auto lg:flex-col">
                    {pages.map((p, i) => {
                        const done = i < step && missingOnPage(p, run.answers).length === 0
                        return (
                            <button
                                key={p.id}
                                type="button"
                                onClick={() => run.goTo(i)}
                                className={cn(
                                    'flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                                    i === step ? 'bg-primary/10 font-medium text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                                )}
                            >
                                <span className={cn('flex size-5 shrink-0 items-center justify-center rounded-full border text-[10px]',
                                    done ? 'border-primary bg-primary text-primary-foreground' : 'border-border')}>
                                    {done ? <Check className="size-3" /> : i + 1}
                                </span>
                                <span className="truncate">{p.title || `Step ${i + 1}`}</span>
                            </button>
                        )
                    })}
                    <button
                        type="button"
                        onClick={() => run.goTo(pages.length)}
                        className={cn('flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                            onReview ? 'bg-primary/10 font-medium text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground')}
                    >
                        <span className="flex size-5 shrink-0 items-center justify-center rounded-full border text-[10px]"><Send className="size-3" /></span>
                        Review &amp; submit
                    </button>
                </nav>
            </aside>

            <Card>
                <CardHeader className="gap-3">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <CardTitle className="text-lg">{onReview ? 'Review & submit' : page?.title}</CardTitle>
                            {!onReview && page?.description && <CardDescription>{page.description}</CardDescription>}
                        </div>
                        <SaveIndicator status={run.saveStatus} />
                    </div>
                    {step === 0 && !onReview && (
                        <Field>
                            <FieldLabel htmlFor="submission-title">Reference title <span className="font-normal text-muted-foreground">(optional)</span></FieldLabel>
                            <Input id="submission-title" value={run.title} maxLength={120} className="max-w-md"
                                placeholder="A short name to find this later" onChange={(e) => run.setTitle(e.target.value)} />
                        </Field>
                    )}
                </CardHeader>
                <CardContent className="flex flex-col gap-6">
                    {returned && (
                        <Alert className="border-orange-500/50 bg-orange-500/5">
                            <AlertTriangle />
                            <AlertTitle>This request was sent back to you</AlertTitle>
                            <AlertDescription>{sentBackReason || 'Update your answers, then resubmit.'}</AlertDescription>
                        </Alert>
                    )}
                    {onReview ? (
                        <>
                            {run.allMissing.length > 0 && (
                                <Alert variant="destructive">
                                    <AlertTriangle />
                                    <AlertTitle>{run.allMissing.length} required {run.allMissing.length === 1 ? 'question is' : 'questions are'} unanswered</AlertTitle>
                                    <AlertDescription>Complete them before submitting.</AlertDescription>
                                </Alert>
                            )}
                            {run.submitError && (
                                <Alert variant="destructive"><AlertTriangle /><AlertTitle>Could not submit</AlertTitle>
                                    <AlertDescription>{run.submitError}</AlertDescription></Alert>
                            )}
                            <AnswersView definition={run.definition} answers={run.answers} files={files} submissionId={submissionId} fileAccess="normal" />
                        </>
                    ) : page ? (
                        <div className="max-w-3xl"><FormRenderer submissionId={submissionId} page={page} answers={run.answers} onChange={run.setAnswer} invalidKeys={run.invalidKeys} /></div>
                    ) : null}

                    <div className="flex items-center justify-between border-t pt-5">
                        <Button variant="outline" onClick={run.back} disabled={step === 0}>
                            <ArrowLeft data-icon="inline-start" /> Back
                        </Button>
                        {onReview ? (
                            <Button onClick={onSubmit} disabled={run.submitting || run.allMissing.length > 0}>
                                {run.submitting ? <Spinner data-icon="inline-start" /> : <Send data-icon="inline-start" />}
                                {returned ? 'Resubmit' : 'Submit'}
                            </Button>
                        ) : (
                            <Button onClick={() => run.next()}>
                                {step === pages.length - 1 ? 'Review' : 'Next'} <ArrowRight data-icon="inline-end" />
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}
